import {
  VisitEventStatusEnum,
  DriveTimeOptimizerQueryVariables,
  CalendarBlockEventStatusEnum,
  ShiftStatusEnum,
  ShiftProviderTravelEnum,
  DriveTimeOptimizerVisitRequestInput,
  CommandIndexProvidersQuery,
  CommandIndexCareRequirementVisitsQuery,
  DriveTimeOptimizerShiftInput,
  OptimizerFriction as GraphqlOptimizerFriction,
} from "~/graphql/__generated__/graphql";
import { OrgConfig } from "~/providers/OrgConfigProvider";
import { LocalCalendarBlock, LocalShift, LocalVisit, LocalVisitResource } from "~/hooks/visitDragDropReducer";
import { visitResourceStatus } from "~/utils/statuses";
import { compact, groupBy, max, min, minBy, pick, partition } from "lodash";
import { CalendarBlockConflict, calendarBlockIsFilter, isAllowedInBlock } from "@caredna-marlin/utils/calendarBlockHelper";
import { OptimizerFriction, OptimizerPreSkipReason } from "@caredna-marlin/constraint-optimizer/types";
import { validateProviderLicense } from "./visitValidationUtils";
import { findAllServiceLinesForVisitType } from "~/hooks/visitDragDropUtils";
import { getShiftStartEndLocation, getVirtualLocation, getVisitResourceLocation } from "@caredna-marlin/utils/locations";
import { getPatientDayStartUtc, getUtcEquivalentOfPatientWallTime } from "@caredna-marlin/utils/dates";
import { DEFAULT_SHUFFLER_LEAD_TIME_MINUTES } from "@caredna-marlin/utils/organizationTypes";
import { addHours } from "date-fns";
import {
  durationMinutes,
  getConstrainedErrandArrivalBand,
  getErrandAnchorResource,
  getErrandProximityConstraint,
  getErrandRelativeToVisit,
  getNonErrandSiblingOffsets,
  getPrecedingResourceId,
  isErrandVisitResource,
  sortVisitResourcesByStartTime,
} from "./driveTimeOptimizerErrandConversion";

export const MAX_IN_HOME_DISTANCE_MILES = 150;

export const effectiveShufflerLeadTimeMs = (configuredLeadTimeMinutes: number | undefined): number =>
  (configuredLeadTimeMinutes ?? DEFAULT_SHUFFLER_LEAD_TIME_MINUTES) * 60_000;

export type LocalShiftWithShiftCenter = LocalShift & {
  shiftCenter: { addressCoordinates: string | null } | null,
};

export type LocalVisitResourceFriction = OptimizerFriction | "frozen";

type VisitResourceFrictionFields = {
  startTimeFriction: LocalVisitResourceFriction;
  assignmentFriction: LocalVisitResourceFriction;
};

export type LocalVisitResourceWithOptionalFriction = LocalVisitResource & Partial<VisitResourceFrictionFields>;

export type LocalVisitResourceWithFriction = LocalVisitResource & VisitResourceFrictionFields;

const optimizerFrictionForResource = (
  friction: LocalVisitResourceFriction,
): OptimizerFriction => friction === "frozen" ? "already_filtered" : friction;

const pointArrivalWindow = (startTime: string): DriveTimeOptimizerArrivalWindow => {
  const fixedStart = new Date(startTime).toISOString();
  return { arrivalWindowStart: fixedStart, arrivalWindowEnd: fixedStart };
};

export type OptimizerInitialState = {
  schedulingCutoff: Date;
  eligibleShifts: LocalShiftWithShiftCenter[];
  pastVisitResources: Pick<LocalVisitResource, 'id' | 'startTime' | 'visitId'>[];
  constrainedVisitResources: LocalVisitResource[];
  optimizableVisitResources: LocalVisitResource[];
  localCalendarBlocks: LocalCalendarBlock[];
  careRequirementVisits: CommandIndexCareRequirementVisitsQuery["visit"];
  shufflerLeadTime: number | undefined;
  arrivalWindowAdjustment: number | undefined;
  providers: CommandIndexProvidersQuery["commandIndexProviders"]["providers"];
  orgConfig: Pick<OrgConfig, 'visitResourceRequirements' | 'providerTypes' | 'visitTypes' | 'serviceLines' | 'shiftCenters' | 'teamProvider' | 'errandTypes' | 'errandTypeByRequirementId'>;
};

export const parseLatLng = (coords: string | null | undefined): [number, number] | null => {
  if (!coords) return null;
  const [latStr, lngStr] = coords.split(",");
  const lat = Number(latStr);
  const lng = Number(lngStr);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return [lat, lng];
};

// This is a fast equirectangular approximation of the haversine distance between two points.
// Off by less than 0.2 miles across a 150 mile radius within the contiguous US.
export const fastHaversineApproxMiles = (originCoords: [number, number], destinationCoords: [number, number]): number => {
  const [oLat, oLng] = originCoords;
  const [dLat, dLng] = destinationCoords;
  const meanLatRadians = ((oLat + dLat) / 2) * (Math.PI / 180);
  const x = (dLng - oLng) * Math.cos(meanLatRadians);
  const y = dLat - oLat;
  return Math.sqrt(x * x + y * y) * 69.0;
};

type DriveOptimizerProviderSelection = {
  acceptableProviderIds: number[];
  careTeamProviderIds: number[];
  preSkipReasonNoProviders?: OptimizerPreSkipReason;
};

export const getAcceptableProviderIdsForDriveOptimizer = (
  resource: LocalVisitResource,
  orgConfig: OrgConfig,
  availableProviderIdsByType: Record<number, number[]>,
  providersById: Record<number, CommandIndexProvidersQuery["commandIndexProviders"]["providers"][number]>,
): DriveOptimizerProviderSelection => {
  const requirement = resource.visitResourceRequirementId
    ? orgConfig.visitResourceRequirements[resource.visitResourceRequirementId]
    : undefined;

  const serviceLineIdsForVisitType = compact(
    findAllServiceLinesForVisitType(resource.visit.visitTypeId, orgConfig)
      .map((serviceLine) => serviceLine?.id)
  );

  const acceptableProviderTypeIds = requirement?.providerTypes.map((pt) => pt.id) ?? [];
  if (acceptableProviderTypeIds.length === 0) {
    return {
      acceptableProviderIds: [],
      careTeamProviderIds: [],
      preSkipReasonNoProviders: "MISCONFIGURED_PROVIDER_REQUIREMENTS",
    };
  }

  const providersMatchingType = acceptableProviderTypeIds.flatMap((typeId) => {
    return (availableProviderIdsByType[typeId] ?? []).filter((providerId) => !!providersById[providerId]);
  });

  const providersMatchingServiceLine = providersMatchingType.filter((providerId) => {
    const provider = providersById[providerId];
    return provider.serviceLineProviders.some((slp) => serviceLineIdsForVisitType.includes(slp.serviceLineId));
  });

  const providersMatchingLicense = providersMatchingServiceLine.filter((providerId) => {
    const provider = providersById[providerId];
    const providerType = requirement?.providerTypes.find((pt) => pt.id === provider.providerTypeId) ?? orgConfig.providerTypes[provider.providerTypeId];
    const requiresStateLicense = providerType?.requireStateLicense === true;

    if (!requiresStateLicense) {
      return true;
    }

    return validateProviderLicense(
      provider,
      resource.visit.addressState,
      orgConfig,
      requirement ? [requirement] : []
    ).length === 0;
  });

  let preSkipReasonNoProviders: OptimizerPreSkipReason | undefined;
  if (providersMatchingType.length === 0) {
    preSkipReasonNoProviders = "PROVIDER_TYPE";
  } else if (providersMatchingServiceLine.length === 0) {
    preSkipReasonNoProviders = "SERVICE_LINE";
  } else if (providersMatchingLicense.length === 0) {
    preSkipReasonNoProviders = "STATE_LICENSE";
  }

  // Care-team providers are a preference, not a hard eligibility filter.
  const acceptableCareTeamProviders = resource.visit.patient?.careTeamProviders?.filter((ctp) => acceptableProviderTypeIds.includes(ctp.providerTypeId))
  const acceptableProviderIds = Array.from(new Set(providersMatchingLicense));
  const careTeamProviderIds = compact(
    Array.from(new Set(acceptableCareTeamProviders?.map((ctp) => ctp.providerId) ?? []))
  ).filter((providerId) => acceptableProviderIds.includes(providerId));
  
  return {
    acceptableProviderIds,
    careTeamProviderIds,
    preSkipReasonNoProviders,
  };
};

// This groups locations by provider.
// For in-home, or if all virtual providers have the same home location, will return one element.
export function buildVisitLocations(
  acceptableShifts: LocalShiftWithShiftCenter[],
  inHomeLocation: string,
  inHome: boolean
): { location: string; providerIds: number[] }[] {

  if (inHome) {
    return [{ location: inHomeLocation, providerIds: Array.from(new Set(acceptableShifts.map(s => s.providerId))) }];
  }

  const locationRecord: Record<string, number[]> = {};

  acceptableShifts.forEach((s) => {
    const loc = getVirtualLocation(s.provider ?? {}, s);
    if (!locationRecord[loc]) locationRecord[loc] = [];
    locationRecord[loc].push(s.providerId);
  });

  return Object.entries(locationRecord).map(([location, providerIds]) => ({
    location,
    providerIds,
  }));
}

const getAvailableProviderIdsByType = (shifts: LocalShift[]): Record<number, number[]> => {
  return shifts.reduce(
    (acc, shift) => {
      if (shift.provider && shift.provider.active) {
        const typeId = shift.provider.providerTypeId;
        if (!acc[typeId]) {
          acc[typeId] = [];
        }
        if (!acc[typeId].includes(shift.provider.id)) {
          acc[typeId].push(shift.provider.id);
        }
      }
      return acc;
    },
    {} as Record<number, number[]>
  );
}

type ServiceLineSchedulingFlags = {
  ignoreDriveTimeShiftStart: boolean;
  ignoreDriveTimeShiftEnd: boolean;
};

export const getMergedIgnoreDriveTimeShiftFlags = (orgConfig: OrgConfig): ServiceLineSchedulingFlags => {
  let ignoreStart = false;
  let ignoreEnd = false;

  const serviceLines = orgConfig.serviceLines;

  for (const serviceLine of Object.values(serviceLines)) {
    if (!serviceLine?.schedulingConfig) continue;

    const config = serviceLine.schedulingConfig;

    if (config.ignoreDriveTimeShiftStart === true) {
      ignoreStart = true;
    }
    if (config.ignoreDriveTimeShiftEnd === true) {
      ignoreEnd = true;
    }

    if (ignoreStart && ignoreEnd) break;
  }

  return {
    ignoreDriveTimeShiftStart: ignoreStart,
    ignoreDriveTimeShiftEnd: ignoreEnd,
  };
}

export type DriveTimeOptimizerArrivalWindow = {arrivalWindowStart: string, arrivalWindowEnd: string}

export type CareRequirementVisitOverride = {
  arrivalWindowStart?: string;
  arrivalWindowEnd?: string;
  locked: boolean;
  // Start time is frozen and assignment is not. The window is the current visit
  // start so a later requirement stays fixed, while this visit stays assignable.
  // Unfrozen siblings do not inherit this instant; offsets keep them aligned.
  timeFixed?: boolean;
};

export type CareRequirementProximityConstraint = {
  earlierVisitResourceId: number;
  laterVisitResourceId: number;
  minProximityMinutes?: number;
  maxProximityMinutes?: number;
};

export type ProcessCareRequirementsResult = {
  careRequirementOverridesByVisitId: Record<number, CareRequirementVisitOverride>;
  proximityConstraints: CareRequirementProximityConstraint[];
};

const upsertCareRequirementOverride = (
  careRequirementOverridesByVisitId: Record<number, CareRequirementVisitOverride>,
  visitId: number,
  next: {
    arrivalWindowStart?: Date;
    arrivalWindowEnd?: Date;
    locked?: boolean;
  }
) => {
  const current = careRequirementOverridesByVisitId[visitId] ?? { locked: false };

  const nextStart = max(
    compact([
      current.arrivalWindowStart ? new Date(current.arrivalWindowStart) : undefined,
      next.arrivalWindowStart,
    ])
  );
  const nextEnd = min(
    compact([
      current.arrivalWindowEnd ? new Date(current.arrivalWindowEnd) : undefined,
      next.arrivalWindowEnd,
    ])
  );

  careRequirementOverridesByVisitId[visitId] = {
    ...(nextStart ? { arrivalWindowStart: nextStart.toISOString() } : {}),
    ...(nextEnd ? { arrivalWindowEnd: nextEnd.toISOString() } : {}),
    locked: next.locked ?? current.locked,
  };
};

export const getCareReqVisitSortKey = (visit: CommandIndexCareRequirementVisitsQuery["visit"][number], patientTimezone: string): string => {
  const requirementStart = new Date(visit.startTime);
  const patientDayStart = getPatientDayStartUtc(requirementStart, patientTimezone);
  return `${patientDayStart.toISOString()}-${visit.careRequirement?.id ?? 0}`;
}

export const isBeforeSchedulingArrivalCutoff = (
  resource: Pick<LocalVisitResource, "startTime" | "startOfShiftDriveTime" | "interVisitDriveTime">,
  now: Date,
): boolean => {
  const driveTimeMinutes = resource.startOfShiftDriveTime + resource.interVisitDriveTime;
  const arrivalTime = now.getTime() + driveTimeMinutes * 60_000;
  return new Date(resource.startTime).getTime() < arrivalTime;
};

export const processCareRequirements = (
  careRequirementVisits: CommandIndexCareRequirementVisitsQuery["visit"],
  now: Date,
  localVisitResources: LocalVisitResourceWithOptionalFriction[],
  isErrandResource: (resource: LocalVisitResource) => boolean = () => false,
): ProcessCareRequirementsResult => {
  const careRequirementOverridesByVisitId: Record<number, CareRequirementVisitOverride> = {};
  const proximityConstraints: CareRequirementProximityConstraint[] = [];
  const visitResourcesByVisitId = groupBy(localVisitResources, "visitId");
  const visitIdsInCurrentView = new Set(localVisitResources.map(vr => vr.visitId));
  const earliestResourceByVisitId: Record<number, LocalVisitResource | undefined> = Object.fromEntries( 
    Object.entries(visitResourcesByVisitId).map(([visitId, resources]) => [
      Number(visitId),
      minBy(resources.filter((resource) => !isErrandResource(resource)), "startTime"),
    ])
  );

  // Update careRequirementVisits to include latest state from drag/drop edits in localVisitResources.
  careRequirementVisits = careRequirementVisits.map(visit => {
    const earliestResource = earliestResourceByVisitId[visit.id];
    if (!earliestResource) return visit;

    return {
      ...visit,
      startTime: earliestResource.startTime,
      endTime: earliestResource.endTime,
      manualEdits: earliestResource.visit.manualEdits,
    };
  });

  // ignore any care requirements that don't affect the visits we are optimizing
  // TODO: Instead of filtering, this whole function might be better re-written to loop over local visit resources and calculate care requirement overrides and proximity constraints for each.
  const patientIds = new Set(localVisitResources.map((resource) => resource.visit.patient?.id));
  careRequirementVisits = careRequirementVisits.filter(visit => patientIds.has(visit.patientId));


  // Locked visits leave the run. Time-fixed visits stay assignable, but their start cannot move.
  const lockedVisits: Set<number> = new Set();
  const timeFixedVisits: Set<number> = new Set();
  careRequirementVisits.forEach(visit => {
    const earliestResource = earliestResourceByVisitId[visit.id];
    const beforeCutoff = earliestResource
      ? isBeforeSchedulingArrivalCutoff(earliestResource, now)
      : new Date(visit.startTime) < now;
    const nonErrandResources = (visitResourcesByVisitId[visit.id] ?? []).filter((resource) => !isErrandResource(resource));
    const fullyFrozen = nonErrandResources.some((resource) =>
      resource.startTimeFriction === "frozen" && resource.assignmentFriction === "frozen"
    );
    const startTimeFrozen = nonErrandResources.some((resource) =>
      resource.startTimeFriction === "frozen"
    );

    if (visit.manualEdits || beforeCutoff || !visitIdsInCurrentView.has(visit.id) || fullyFrozen) {
      lockedVisits.add(visit.id);
    } else if (startTimeFrozen) {
      timeFixedVisits.add(visit.id);
    }
  });

  const visitsByPatientId = groupBy(careRequirementVisits, "patientId");

  Object.values(visitsByPatientId).forEach((patientVisits) => {
    const patientTimezone =
      patientVisits
        .map(v => earliestResourceByVisitId[v.id]?.visit.addressTimezone)
        .find(Boolean) || "UTC";
    // NOTE: We sort by care requirement id, regardless of current visit start time.
    const sortedVisits = [...patientVisits].sort(
      (left, right) => { return getCareReqVisitSortKey(left, patientTimezone).localeCompare(getCareReqVisitSortKey(right, patientTimezone)) }
    );

    sortedVisits.forEach((requirementVisit, index) => {
      const earliestResource = earliestResourceByVisitId[requirementVisit.id];
      if (!earliestResource) return;
      
      const requirementStart = new Date(requirementVisit.startTime);
      // Locked visits are removed from the run at their current start.
      if (lockedVisits.has(requirementVisit.id)) {
        upsertCareRequirementOverride(careRequirementOverridesByVisitId, requirementVisit.id, {
          arrivalWindowStart: requirementStart,
          arrivalWindowEnd: requirementStart,
          locked: true,
        });
        return;
      }

      // Start-time freeze keeps the visit assignable, but later requirements
      // still treat this start as immovable. Do not widen with patient-day bounds.
      if (timeFixedVisits.has(requirementVisit.id)) {
        upsertCareRequirementOverride(careRequirementOverridesByVisitId, requirementVisit.id, {
          arrivalWindowStart: requirementStart,
          arrivalWindowEnd: requirementStart,
          locked: false,
        });
        careRequirementOverridesByVisitId[requirementVisit.id].timeFixed = true;
        return;
      }

      // The remainder of the logic is care requirement specific
      const careRequirement = requirementVisit.careRequirement;
      if (!careRequirement) return;

      const timezone = earliestResource.visit.addressTimezone || "UTC";
      const patientDayStart = getUtcEquivalentOfPatientWallTime(requirementStart, "00:00", timezone);
      const patientDayEnd = new Date(addHours(patientDayStart, 24).getTime() - 1);

      // For unlocked visits, start with a full patient-day window, then tighten with other constraints.
      upsertCareRequirementOverride(careRequirementOverridesByVisitId, requirementVisit.id, {
        arrivalWindowStart: patientDayStart,
        arrivalWindowEnd: patientDayEnd,
      });

      // Handle constraints on earliest and latest start times.
      const wallTimeArrivalWindowStart = careRequirement.earliestStartTime
        ? getUtcEquivalentOfPatientWallTime(requirementStart, careRequirement.earliestStartTime, timezone)
        : undefined;
      const wallTimeArrivalWindowEnd = careRequirement.latestStartTime
        ? getUtcEquivalentOfPatientWallTime(requirementStart, careRequirement.latestStartTime, timezone)
        : undefined;

      if (wallTimeArrivalWindowStart || wallTimeArrivalWindowEnd) {
        upsertCareRequirementOverride(careRequirementOverridesByVisitId, requirementVisit.id, {
          arrivalWindowStart: wallTimeArrivalWindowStart,
          arrivalWindowEnd: wallTimeArrivalWindowEnd,
        });
      }

      const hasOffsetConstraint =
        careRequirement.minTimeFromLastVisit != null || careRequirement.maxTimeFromLastVisit != null;
      if (!hasOffsetConstraint || index === 0) return;
      const previousRequirementVisit = sortedVisits[index - 1];
      
      // If the previous visit can't be moved, then we can convert the proximity constraint to a fixed arrival window.
      if (lockedVisits.has(previousRequirementVisit.id) || timeFixedVisits.has(previousRequirementVisit.id)) {
        const previousRequirementStart = new Date(previousRequirementVisit.startTime);
        const minArrivalWindowStart = careRequirement.minTimeFromLastVisit == null
          ? undefined
          : new Date(previousRequirementStart.getTime() + careRequirement.minTimeFromLastVisit * 60_000);
        const maxArrivalWindowEnd = careRequirement.maxTimeFromLastVisit == null
          ? undefined
          : new Date(previousRequirementStart.getTime() + careRequirement.maxTimeFromLastVisit * 60_000);
        upsertCareRequirementOverride(careRequirementOverridesByVisitId, requirementVisit.id, {
          arrivalWindowStart: minArrivalWindowStart,
          arrivalWindowEnd: maxArrivalWindowEnd,
        });
        return;
      }

      // If the previous visit can be moved, then we need to create a proximity constraint.
      const previousResourceId = earliestResourceByVisitId[previousRequirementVisit.id]?.id;
      const currentResourceId = earliestResourceByVisitId[requirementVisit.id]?.id;
      if (!previousResourceId || !currentResourceId) return;

      proximityConstraints.push({
        earlierVisitResourceId: previousResourceId,
        laterVisitResourceId: currentResourceId,
        ...(careRequirement.minTimeFromLastVisit != null
          ? { minProximityMinutes: careRequirement.minTimeFromLastVisit }
          : {}),
        ...(careRequirement.maxTimeFromLastVisit != null
          ? { maxProximityMinutes: careRequirement.maxTimeFromLastVisit }
          : {}),
      });
    });
  });

  return {
    careRequirementOverridesByVisitId,
    proximityConstraints,
  };
};

const getUnconstrainedArrivalWindow = (shifts: LocalShift[]): DriveTimeOptimizerArrivalWindow => {
  if (!shifts.length) {
    const now = new Date().toISOString();
    return { arrivalWindowStart: now, arrivalWindowEnd: now };
  }

  let earliestStart = new Date(shifts[0].startTime);
  let latestEnd = new Date(shifts[0].endTime);

  for (const shift of shifts) {
    const start = new Date(shift.startTime);
    const end = new Date(shift.endTime);
    if (start < earliestStart) earliestStart = start;
    if (end > latestEnd) latestEnd = end;
  }

  return { arrivalWindowStart: earliestStart.toISOString(), arrivalWindowEnd: latestEnd.toISOString() };
};

export const getArrivalWindow = (
  resource: LocalVisitResource,
  unconstrainedArrivalWindow: DriveTimeOptimizerArrivalWindow,
  arrivalWindowAdjustment: number | undefined
): DriveTimeOptimizerArrivalWindow => {
  const { visit } = resource;

  const rStart = new Date(resource.startTime);
  const vStart = new Date(visit.startTime);
  let arrivalStart = new Date(visit.arrivalWindowStartTime);
  let arrivalEnd = new Date(visit.arrivalWindowEndTime);

  if (arrivalWindowAdjustment !== undefined) {
    const arrivalWindowHalfMs = (arrivalWindowAdjustment / 2) * 60_000;
    arrivalStart = new Date(vStart.getTime() - arrivalWindowHalfMs)
    arrivalEnd = new Date(vStart.getTime() + arrivalWindowHalfMs)
  }

  if (vStart < arrivalStart || vStart > arrivalEnd) {
    return unconstrainedArrivalWindow;
  }

  // Offset in minutes between resource start and base visit start
  const offsetMs = rStart.getTime() - vStart.getTime();

  // Arrival window shifted by this offset
  const adjustedStart = new Date(arrivalStart.getTime() + offsetMs);
  const adjustedEnd = new Date(arrivalEnd.getTime() + offsetMs);

  // Determine whether the resource's start time falls inside the adjusted window
  if (rStart >= adjustedStart && rStart <= adjustedEnd) {
    return {
      arrivalWindowStart: adjustedStart.toISOString(),
      arrivalWindowEnd: adjustedEnd.toISOString(),
    };
  }

  return unconstrainedArrivalWindow;
};

type driveTimeOptimizerResolverInput = DriveTimeOptimizerQueryVariables['input']

export type DriveTimeOptimizerRequestData = {
  input: driveTimeOptimizerResolverInput;
  optimizerLogInitialState: OptimizerInitialState;
}

const isConstrainedToCurrentPlacement = (
  resource: LocalVisitResource,
  careRequirementOverride?: { locked?: boolean },
): boolean => {
  return visitResourceStatus(resource) !== VisitEventStatusEnum.Scheduled
    || resource.visit.manualEdits
    || !!careRequirementOverride?.locked;
};

const partitionFutureVisitResources = (
  futureVisitResources: LocalVisitResourceWithFriction[],
  careRequirementOverridesByVisitId: Record<number, { locked?: boolean }>,
): [LocalVisitResourceWithFriction[], LocalVisitResourceWithFriction[]] => {
  const constrainedVisitResources: LocalVisitResourceWithFriction[] = [];
  const optimizableVisitResources: LocalVisitResourceWithFriction[] = [];

  for (const resource of futureVisitResources) {
    if (
      isConstrainedToCurrentPlacement(resource, careRequirementOverridesByVisitId[resource.visitId])
      || (resource.startTimeFriction === "frozen" && resource.assignmentFriction === "frozen")
    ) {
      constrainedVisitResources.push(resource);
    } else {
      optimizableVisitResources.push(resource);
    }
  }

  return [constrainedVisitResources, optimizableVisitResources];
};

const constrainNonErrandSiblings = (
  constrainedVisitResources: LocalVisitResourceWithFriction[],
  optimizableVisitResources: LocalVisitResourceWithFriction[],
  isErrandResource: (resource: LocalVisitResource) => boolean,
): [LocalVisitResourceWithFriction[], LocalVisitResourceWithFriction[]] => {
  const constrainedVisitIds = new Set(
    constrainedVisitResources
      .filter((resource) => !isErrandResource(resource))
      .map((resource) => resource.visitId),
  );
  if (constrainedVisitIds.size === 0) {
    return [constrainedVisitResources, optimizableVisitResources];
  }

  const pulledSiblings: LocalVisitResourceWithFriction[] = [];
  const stillOptimizable: LocalVisitResourceWithFriction[] = [];
  for (const resource of optimizableVisitResources) {
    if (!isErrandResource(resource) && constrainedVisitIds.has(resource.visitId)) {
      pulledSiblings.push(resource);
    } else {
      stillOptimizable.push(resource);
    }
  }

  return [[...constrainedVisitResources, ...pulledSiblings], stillOptimizable];
};

const boundaryLocationForVisitResource = (
  resource: LocalVisitResource,
  shift: LocalShiftWithShiftCenter,
  visitResourceRequirements: OrgConfig["visitResourceRequirements"],
): string => {
  const inHome = resource.visitResourceRequirementId
    ? visitResourceRequirements[resource.visitResourceRequirementId]?.inHome ?? true
    : true;
  return getVisitResourceLocation(
    {
      addressCoordinates: resource.addressCoordinates,
      visit: resource.visit,
      resourceRequirement: { inHome },
    },
    shift.provider ?? {},
    shift,
  );
};

export const splitShiftByConstrainedVisitResource = (
  shift: DriveTimeOptimizerShiftInput,
  resource: LocalVisitResource,
  boundaryLocation: string,
): DriveTimeOptimizerShiftInput[] => {
  const overlapping = shift.startTime < resource.endTime && shift.endTime > resource.startTime && shift.providerId == resource.providerId;
  if (!overlapping) return [shift];
  const splitShifts: DriveTimeOptimizerShiftInput[] = [];
  const minimumShiftDurationMinutes = 15;

  // New shift from start of shift to start of resource
  if (new Date(shift.startTime).getTime() < new Date(resource.startTime).getTime() - minimumShiftDurationMinutes * 60_000) {
    splitShifts.push({
      ...shift,
      endTime: resource.startTime,
      endLocation: boundaryLocation || shift.endLocation,
      endsShift: false,
    });
  }

  if (new Date(shift.endTime).getTime() > new Date(resource.endTime).getTime() + minimumShiftDurationMinutes * 60_000) {
    splitShifts.push({
      ...shift,
      startTime: resource.endTime,
      startLocation: boundaryLocation || shift.startLocation,
      startsShift: false,
    });
  }

  return splitShifts;
};

type FilterBlockInput = {
    providerId: number,
    startTime: string,
    endTime: string,
    location: string,
    includedIds: number[],
    excludedIds: number[],
  }

const buildFilterBlockInputs = (visitResources: LocalVisitResource[], filteringCalendarBlocks: LocalCalendarBlock[], visitRequests: DriveTimeOptimizerVisitRequestInput[]): FilterBlockInput[] => {

  const convertedCalendarBlocks = filteringCalendarBlocks.map(cb => {
    return {
      ...cb,
      startTime: new Date(cb.startTime),
      endTime: new Date(cb.endTime),
    }
  })

  const convertedVisitResources = visitResources.map(vr => {
    const visitRequest = visitRequests.find(request => request.visitResourceId == vr.id)
    return {
      ...vr,
      durationMs: new Date(vr.endTime).getTime() - new Date(vr.startTime).getTime(),
      visit: {
        ...vr.visit,
        arrivalWindowStartTime: new Date(vr.visit.arrivalWindowStartTime),
        arrivalWindowEndTime: new Date(vr.visit.arrivalWindowEndTime),
      },
      acceptableProviderIds: visitRequest?.locations?.flatMap(loc => loc.providerIds) ?? [],
    }
  })

  const filterBlocks = convertedCalendarBlocks.map((cb) => {
    const possibleVisitResources = convertedVisitResources.filter(vr => {
      const adjustedStart = new Date(cb.startTime.getTime() - vr.durationMs)
      // Visits can be contiguous with a blocking filter, so we don't need to include if boundaries are equal.
      return  vr.visit.arrivalWindowEndTime > adjustedStart &&
        vr.visit.arrivalWindowStartTime < cb.endTime && 
        vr.acceptableProviderIds.includes(cb.providerId)
    })

    const blockCheck = (vr: typeof convertedVisitResources[number]) => isAllowedInBlock(vr.visit.visitTypeId, vr.visit, cb) == CalendarBlockConflict.ALLOWED

    const [included, excluded] = partition(possibleVisitResources, blockCheck)

    return {
      providerId: cb.providerId,
      startTime: cb.startTime.toISOString(),
      endTime: cb.endTime.toISOString(),
      location: cb.addressCoordinates || '',
      includedIds: included.map(vr => vr.id),
      excludedIds: excluded.map(vr => vr.id),
    }
  });

  return filterBlocks;
}


const graphqlFriction: Record<OptimizerFriction, GraphqlOptimizerFriction> = {
  none: GraphqlOptimizerFriction.None,
  low: GraphqlOptimizerFriction.Low,
  medium: GraphqlOptimizerFriction.Medium,
  high: GraphqlOptimizerFriction.High,
  already_filtered: GraphqlOptimizerFriction.AlreadyFiltered,
};

const changeFrictionFor = (
  startTimeFriction: OptimizerFriction,
  assignmentFriction: OptimizerFriction,
  currentStartTime: string | null | undefined,
  currentShiftIds: number[],
) => ({
  currentStartTime: currentStartTime ?? null,
  currentShiftIds,
  startTimeFriction: graphqlFriction[startTimeFriction],
  assignmentFriction: graphqlFriction[assignmentFriction],
});

const currentShiftIdsForProvider = (
  allowedShifts: { id: number; providerId: number | null | undefined }[],
  providerId: number | null | undefined,
): number[] => {
  if (providerId == null) return [];
  return allowedShifts
    .filter((shift) => shift.providerId === providerId)
    .map((shift) => shift.id);
};

export const buildDriveOptimizerRequestData = (
  localVisits: LocalVisit[],
  localCalendarBlocks: LocalCalendarBlock[],
  localVisitResourcesWithOptionalFriction: LocalVisitResourceWithOptionalFriction[],
  localShifts: LocalShift[],
  careRequirementVisits: CommandIndexCareRequirementVisitsQuery["visit"],
  orgConfig: OrgConfig,
  shufflerLeadTime: number | undefined,
  arrivalWindowAdjustment: number | undefined,
  providers: CommandIndexProvidersQuery["commandIndexProviders"]["providers"],
  defaultStartTimeFriction: LocalVisitResourceFriction = "none",
  defaultAssignmentFriction: LocalVisitResourceFriction = "none",
): DriveTimeOptimizerRequestData => {

  const localVisitResources: LocalVisitResourceWithFriction[] = localVisitResourcesWithOptionalFriction.map(vr => ({
    ...vr,
    startTimeFriction: vr.startTimeFriction ?? defaultStartTimeFriction,
    assignmentFriction: vr.assignmentFriction ?? defaultAssignmentFriction,
  }));

  // schedulingCutoff and optimizerLogInitialState both use the configured lead time.
  const now = new Date(Date.now() + effectiveShufflerLeadTimeMs(shufflerLeadTime));

  const activeShifts = localShifts.filter((s) => s.status != ShiftStatusEnum.Canceled)

  // Add shift center information that isn't on LocalShifts (for getShiftStartEndLocation)
  const eligibleShifts: LocalShiftWithShiftCenter[] = activeShifts.map((s) => ({
    ...s,
    shiftCenter: s.shiftCenterId ? orgConfig.shiftCenters[s.shiftCenterId] ?? null : null,
  }))

  const eligibleVisitResources = localVisitResources.filter(vr => visitResourceStatus(vr) != VisitEventStatusEnum.Canceled);
  const isErrandResource = (resource: LocalVisitResource) =>
    isErrandVisitResource(resource, orgConfig.errandTypeByRequirementId);

  const { careRequirementOverridesByVisitId, proximityConstraints } = processCareRequirements(
    careRequirementVisits,
    now,
    eligibleVisitResources,
    isErrandResource,
  );

  const availableProviderIdsByType = getAvailableProviderIdsByType(eligibleShifts);
  const providersById = Object.fromEntries(providers.map((provider) => [provider.id, provider]));
  const unconstrainedArrivalWindow = getUnconstrainedArrivalWindow(eligibleShifts);
  const shiftCoordsByShiftId: Record<number, [number, number] | null> = Object.fromEntries(
    eligibleShifts.map((shift) => {
      const shiftLocation = getShiftStartEndLocation(shift.provider ?? {}, shift);
      return [shift.id, parseLatLng(shiftLocation)];
    })
  );

  // WARNING: we don't actually enforce provider to service line associations in most places. And I'm hesitent to load it for
  // every command center page or orgConfig, but I also want this conversion to be a pure function. So just assume all providers
  // are on all service lines for now, and allow ignoring of shift boundary drive times if that is set for any of them.
  const {ignoreDriveTimeShiftStart, ignoreDriveTimeShiftEnd} = getMergedIgnoreDriveTimeShiftFlags(orgConfig);

  // Google skips any visit that can't be reached from the current time, so
  // those need to be locked on the schedule. Several ways to do this, but
  // the cleanest is to not try and optimize it, and adjust the shift.
  const [pastVisitResources, futureVisitResources] = eligibleVisitResources.reduce(
    ([past, future], r) => {
      if (!r.startTime) return [past, future];

      return isBeforeSchedulingArrivalCutoff(r, now)
        ? [[...past, r], future]
        : [past, [...future, r]];
    },
    [[], []] as [LocalVisitResourceWithFriction[], LocalVisitResourceWithFriction[]]
  );

  const [placementConstrainedVisitResources, placementOptimizableVisitResources] =
    partitionFutureVisitResources(
      futureVisitResources,
      careRequirementOverridesByVisitId,
    );
  const [constrainedVisitResources, optimizableVisitResources] = constrainNonErrandSiblings(
    placementConstrainedVisitResources,
    placementOptimizableVisitResources,
    isErrandResource,
  );

  const eligibleProviderIds = new Set([
    ...eligibleShifts.map(s => s.providerId),
    ...compact([...constrainedVisitResources, ...optimizableVisitResources].map(r => r.providerId))
  ])
  const optimizerLogInitialState: OptimizerInitialState = {
    schedulingCutoff: now,
    eligibleShifts,
    pastVisitResources,
    constrainedVisitResources,
    optimizableVisitResources,
    localCalendarBlocks,
    careRequirementVisits: careRequirementVisits.filter(v => v.careRequirement),
    shufflerLeadTime,
    arrivalWindowAdjustment,
    providers: providers.filter(p => eligibleProviderIds.has(p.id)),
    orgConfig: pick(
      orgConfig,
      ['visitResourceRequirements', 'providerTypes', 'visitTypes', 'serviceLines', 'shiftCenters', 'teamProvider', 'errandTypes', 'errandTypeByRequirementId']
    ),
  }

  const optimizableIds = new Set(optimizableVisitResources.map((resource) => resource.id));
  const allVisitResources = [...pastVisitResources, ...constrainedVisitResources, ...optimizableVisitResources];

  const adjustedArrivalWindowLookup: Record<number, DriveTimeOptimizerArrivalWindow> = Object.fromEntries(
    allVisitResources.map((resource): [number, DriveTimeOptimizerArrivalWindow] => {
      // Resources we aren't optimizing keep their current placement, so their window collapses to their start time.
      if (!optimizableIds.has(resource.id)) {
        return [resource.id, pointArrivalWindow(resource.startTime)];
      }

      // A frozen start is a hard point. Care-requirement windows must not reopen it.
      if (resource.startTimeFriction === "frozen") {
        return [resource.id, pointArrivalWindow(resource.startTime)];
      }

      const baseArrivalWindow = getArrivalWindow(resource, unconstrainedArrivalWindow, arrivalWindowAdjustment);
      const careRequirementOverride = careRequirementOverridesByVisitId[resource.visitId];
      // timeFixed records the earliest resource's start. Later siblings keep their own
      // window and stay aligned with the frozen resource through offsets.
      if (careRequirementOverride?.timeFixed) {
        return [resource.id, baseArrivalWindow];
      }
      return [resource.id, {
        arrivalWindowStart: careRequirementOverride?.arrivalWindowStart ?? baseArrivalWindow.arrivalWindowStart,
        arrivalWindowEnd: careRequirementOverride?.arrivalWindowEnd ?? baseArrivalWindow.arrivalWindowEnd,
      }];
    })
  );

  const sortedOptimizableChains = groupBy(
    sortVisitResourcesByStartTime(optimizableVisitResources.filter((resource) => resource.providerId != null)),
    (resource) => `${resource.visitId}:${resource.providerId}`
  );
  const errandProximityConstraints: CareRequirementProximityConstraint[] = [];

  // These are the visits we will be optimizing
  const providerVisitResources = compact(
    optimizableVisitResources
      .map((resource) => {
        const { acceptableProviderIds, careTeamProviderIds, preSkipReasonNoProviders } = getAcceptableProviderIdsForDriveOptimizer(
          resource,
          orgConfig,
          availableProviderIdsByType,
          providersById
        )
        let preSkipReason: OptimizerPreSkipReason | undefined = preSkipReasonNoProviders;

        if (!resource.visitResourceRequirementId) return false;
        const visitResourceRequirement = orgConfig.visitResourceRequirements[resource.visitResourceRequirementId];
        const inHome = visitResourceRequirement?.inHome ?? true;
        const preserveGroup = visitResourceRequirement?.preserveAutoschedulerGroup ?? false;
        const visitCoords = parseLatLng(
          resource.addressCoordinates || (inHome ? resource.visit.addressCoordinates : null)
        );
        const allowedInOverflow = orgConfig.visitTypes[resource.visit.visitTypeId]?.schedulingConfig?.allowOverflowShifts;
        const resourceIsErrand = isErrandResource(resource);

        const otherResources = localVisits.find(v => v.id == resource.visitId)?.visitResources?.filter(vr => vr.id != resource.id) ?? []
        const offsets = getNonErrandSiblingOffsets(
          resource,
          otherResources,
          resourceIsErrand,
          (other) => isErrandVisitResource(other, orgConfig.errandTypeByRequirementId),
        );
        const precedingResourceId = resource.providerId == null
          ? undefined
          : getPrecedingResourceId(resource.id, sortedOptimizableChains[`${resource.visitId}:${resource.providerId}`] ?? []);

        const careRequirementOverride = careRequirementOverridesByVisitId[resource.visitId];
        let { arrivalWindowStart, arrivalWindowEnd } = adjustedArrivalWindowLookup[resource.id];
        let anchorResourceId: number | undefined;

        if (resourceIsErrand) {
          const errandType = orgConfig.errandTypeByRequirementId[resource.visitResourceRequirementId];
          if (errandType) {
            const relativeToVisit = getErrandRelativeToVisit(errandType.relativeToVisit);
            const anchor = getErrandAnchorResource(resource, allVisitResources, isErrandResource);
            if (!anchor) {
              preSkipReason = preSkipReason ?? "NO_NON_ERRAND_ANCHOR";
            } else {
              const errandDuration = durationMinutes(resource.startTime, resource.endTime);
              const anchorDuration = durationMinutes(anchor.startTime, anchor.endTime);
              if (optimizableIds.has(anchor.id)) {
                anchorResourceId = anchor.id;
                errandProximityConstraints.push(getErrandProximityConstraint(
                  resource.id,
                  errandDuration,
                  anchor.id,
                  anchorDuration,
                  relativeToVisit,
                ));
              }
              const constrainedBand = getConstrainedErrandArrivalBand(
                relativeToVisit,
                adjustedArrivalWindowLookup[anchor.id],
                anchorDuration,
                errandDuration,
              );
              arrivalWindowStart = constrainedBand.arrivalWindowStart;
              arrivalWindowEnd = constrainedBand.arrivalWindowEnd;
            }
          }
        }

        // Errand bands replace the lookup window. Pin after that so a frozen start cannot move.
        if (resource.startTimeFriction === "frozen") {
          const pinned = pointArrivalWindow(resource.startTime);
          arrivalWindowStart = pinned.arrivalWindowStart;
          arrivalWindowEnd = pinned.arrivalWindowEnd;
        }

        const overlappingShifts = eligibleShifts.filter(s => s.startTime <= arrivalWindowEnd && s.endTime >= arrivalWindowStart);
        const acceptableProviderShifts = overlappingShifts.filter((shift) => {
          return acceptableProviderIds.includes(shift.providerId);
        });
        const travelTypeShifts = acceptableProviderShifts.filter((shift) => {
          if (shift.providerTravel == ShiftProviderTravelEnum.InHomeOnly && !inHome) return false;
          if (shift.providerTravel == ShiftProviderTravelEnum.VirtualOnly && inHome) return false;
          return true;
        });
        const overflowMatchingShifts = travelTypeShifts.filter((shift) => {
          return !shift.overflowShift || !!allowedInOverflow;
        });
        const maxInHomeDistanceMatchingShifts = overflowMatchingShifts.filter((shift) => {
          if (!inHome) return true;

          const shiftCoords = shiftCoordsByShiftId[shift.id];
          if (visitCoords && shiftCoords && fastHaversineApproxMiles(visitCoords, shiftCoords) > MAX_IN_HOME_DISTANCE_MILES) {
            return false;
          }

          return true;
        });

        if (!preSkipReason) {
          if (overlappingShifts.length === 0) {
            preSkipReason = "NO_OVERLAPPING_SHIFTS";
          } else if (acceptableProviderShifts.length === 0) {
            preSkipReason = "NO_ACCEPTABLE_PROVIDER_SHIFTS";
          } else if (travelTypeShifts.length === 0) {
            preSkipReason = "NO_TRAVEL_TYPE_SHIFTS";
          } else if (overflowMatchingShifts.length === 0) {
            preSkipReason = "NO_OVERFLOW_FILTER_SHIFTS";
          } else if (maxInHomeDistanceMatchingShifts.length === 0) {
            preSkipReason = "NO_MAX_IN_HOME_DISTANCE_SHIFTS";
          }
        }

        const allowedShifts = maxInHomeDistanceMatchingShifts;
        const preferredShiftIds = preserveGroup && careRequirementOverride
          ? allowedShifts
              .filter((shift) => shift.providerId === resource.providerId)
              .map((shift) => shift.id)
          : allowedShifts
              .filter((shift) => careTeamProviderIds.includes(shift.providerId))
              .map((shift) => shift.id);
        const assignmentFriction = resource.assignmentFriction;
        const currentProviderShiftIds = currentShiftIdsForProvider(allowedShifts, resource.providerId);
        const allowedShiftIds = assignmentFriction === "frozen"
          ? currentProviderShiftIds
          : allowedShifts.map((shift) => shift.id);
        const allowedShiftIdSet = new Set(allowedShiftIds);
        const filteredPreferredShiftIds = preferredShiftIds.filter((shiftId) => allowedShiftIdSet.has(shiftId));

        const visitRequest: DriveTimeOptimizerVisitRequestInput = {
          visitResourceId: resource.id,
          locations: buildVisitLocations(allowedShifts, resource.addressCoordinates || resource.visit.addressCoordinates, inHome),
          startTime: resource.startTime,
          endTime: resource.endTime,
          allowedShiftIds,
          preferredShiftIds: filteredPreferredShiftIds,
          offsets,
          precedingResourceId: precedingResourceId ?? null,
          anchorResourceId: anchorResourceId ?? null,
          locked: false, // Any conflict between locked visits canceles the whole run. Using acceptableProviderIds as softer constraint for now.
          priorityLevel: 2, // Can be used to decide what visits get skipped in favor of other visits.
          arrivalWindowStart,
          arrivalWindowEnd,
          preSkipReason: preSkipReason ?? null,
          tagGroup: resource.visit.episode?.autoschedulerGroup ?? null,
          preserveGroup,
          changeFriction: changeFrictionFor(
            optimizerFrictionForResource(resource.startTimeFriction),
            optimizerFrictionForResource(assignmentFriction),
            resource.startTime,
            currentProviderShiftIds,
          ),
        };

        return visitRequest;
      })
  );

  const filteredLocalCalendarBlocks = localCalendarBlocks.filter(block => {
    return block.eventStatus != CalendarBlockEventStatusEnum.Canceled && 
    block.startTime && block.endTime && block.providerId
  })

  const [filteringBlocks, blockingBlocks] = partition(filteredLocalCalendarBlocks, calendarBlockIsFilter);

  const [blockingBlocksWithAddress, blockingBlocksWithoutAddress] = partition(blockingBlocks, 'addressCoordinates');

  const providerBlockResourcesNoAddresses = compact(
    blockingBlocksWithoutAddress.map((block) => {
      return {
        providerId: block.providerId,
        startTime: block.startTime,
        endTime: block.endTime,
      };
    })
  );

  const providerBlockResourcesWithAddresses = compact(
    blockingBlocksWithAddress.map((block) => {
      if (!block.addressCoordinates) return null;
      const shifts = eligibleShifts.filter(s => s.providerId == block.providerId)

      return {
        visitResourceId: -999, // This is only used for updates, and we never move blocks.
        locations: [{location: block.addressCoordinates, providerIds: [block.providerId]}],
        startTime: block.startTime,
        endTime: block.endTime,
        locked: false, // Constrained by acceptableProviderIds.  TODO: injectedSolutionConstraint is cheaper.
        priorityLevel: 0,
        arrivalWindowStart: block.startTime,
        arrivalWindowEnd: block.startTime,
        offsets: [],
        allowedShiftIds: shifts.map(s => s.id),
        preferredShiftIds: [],
        changeFriction: changeFrictionFor(
          "none",
          "none",
          block.startTime,
          currentShiftIdsForProvider(shifts, block.providerId),
        ),
      };
    })
  );

  const filterBlocks = buildFilterBlockInputs(optimizableVisitResources, filteringBlocks, providerVisitResources)

  const allAcceptableProviderIds = [
    ...providerVisitResources.flatMap((pvr) => pvr.locations.flatMap(loc => loc.providerIds)),
    ...providerBlockResourcesWithAddresses.flatMap((pb) => pb.locations.flatMap(loc => loc.providerIds))
  ]
  
  const constrainedByProviderId = groupBy(constrainedVisitResources, (r) => r.providerId);

  // Google doesn't like visits in the past, so the shift is trimmed and it's location updated to the most recent past visit
  const baseProviderShiftResources: DriveTimeOptimizerShiftInput[] = compact(
    eligibleShifts.map((shift) => {
      const providerId = shift.provider?.id;
      const providerTypeId = shift.provider?.providerTypeId;
      if (!shift.provider || !providerId || !providerTypeId) return null;
      if (!allAcceptableProviderIds.includes(providerId)) return null;
      const defaultShiftLocation = getShiftStartEndLocation(shift.provider, shift);
      const shiftStartTimeParsed = new Date(shift.startTime);
  
      const overlappingPastResources = pastVisitResources.filter(
        (r) => r.providerId === providerId && r.startTime && r.endTime && new Date(r.endTime) > shiftStartTimeParsed
      );
  
      let shiftStartTime = shift.startTime;
      let shiftStartLocation = defaultShiftLocation;
      let startsShift = true;

      if (overlappingPastResources.length > 0) {
        const latest = overlappingPastResources.reduce((acc, curr) => {
          return new Date(curr.endTime) > new Date(acc.endTime) ? curr : acc;
        }, overlappingPastResources[0]);

        shiftStartTime = latest.endTime;
        shiftStartLocation = boundaryLocationForVisitResource(
          latest,
          shift,
          orgConfig.visitResourceRequirements,
        ) || shiftStartLocation;
        // If we moved the shift start time due to a past visit, then it's not the real start of the shift.
        startsShift = false;
      }

      if (shiftStartTime >= shift.endTime) return null;

      return {
        startLocation: shiftStartLocation,
        endLocation: defaultShiftLocation,
        startTime: shiftStartTime,
        endTime: shift.endTime,
        internal: !shift.provider.external,
        shiftId: shift.id,
        overflow: shift.overflowShift,
        providerId,
        providerTypeId,
        startsShift,
        endsShift: true,
        ignoreDriveTimeShiftStart,
        ignoreDriveTimeShiftEnd,
      };
    })
  );

  const eligibleShiftById = new Map(eligibleShifts.map((shift) => [shift.id, shift]));
  const providerShiftResources = baseProviderShiftResources.flatMap((shift) => {
    const constrainedResources = constrainedByProviderId[shift.providerId] ?? [];
    // Split segments still carry the original shift id. Use the original shift
    // when resolving virtual locations so provider and shift-center settings apply.
    const originalShift = eligibleShiftById.get(shift.shiftId);
    let segments = [shift];

    // Each constrained visit punches a hole in every segment it overlaps.
    // The remaining segments become the input for the next constrained visit.
    for (const resource of constrainedResources) {
      const boundaryLocation = originalShift
        ? boundaryLocationForVisitResource(resource, originalShift, orgConfig.visitResourceRequirements)
        : "";
      const nextSegments: DriveTimeOptimizerShiftInput[] = [];
      for (const segment of segments) {
        nextSegments.push(...splitShiftByConstrainedVisitResource(segment, resource, boundaryLocation));
      }
      segments = nextSegments;
    }

    return segments;
  });

  const driveOptimizerInput = {
    visits: [...providerVisitResources, ...providerBlockResourcesWithAddresses],
    breaks: providerBlockResourcesNoAddresses,
    shifts: providerShiftResources,
    filterBlocks,
    proximityConstraints: [...proximityConstraints, ...errandProximityConstraints],
  };

  return {
    input: driveOptimizerInput as driveTimeOptimizerResolverInput,
    optimizerLogInitialState: optimizerLogInitialState,
  };
}


